/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See LICENSE in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { html, render } from './render';
import {
  agents,
  auditEntries,
  decisions,
  evaluatePolicy,
  getChainStatus,
  integrations,
  policyPacks,
  type DecisionStatus,
  type TrustAgent,
} from './trust-data';

type Page = 'overview' | 'agents' | 'decisions' | 'policy' | 'audit' | 'integrations';

export function consumeNavHint(): string | undefined {
  return undefined;
}

export function updateNavBadge(_id: string, _value: string | number): void {
  // Compatibility shim for donor pages that remain in the copied repo.
}

const pageTitles: Record<Page, string> = {
  overview: 'Overview',
  agents: 'Agents',
  decisions: 'Live Decisions',
  policy: 'Policy',
  audit: 'Audit Chain',
  integrations: 'Integrations',
};

let currentPage: Page = 'overview';
let frozenAgentIds = new Set(agents.filter(agent => agent.status === 'frozen').map(agent => agent.id));
let auditTampered = false;
let playground = {
  operation: 'reconcile_invoice',
  amount: 42,
  resource: 'invoice:INV-2048',
};

function content(): HTMLElement {
  const el = document.getElementById('content');
  if (!el) throw new Error('Missing #content root');
  return el;
}

function statusClass(status: DecisionStatus | string): string {
  if (status === 'allow' || status === 'configured' || status === 'valid' || status === 'active') return 'trust-ok';
  if (status === 'deny' || status === 'broken' || status === 'frozen') return 'trust-bad';
  return 'trust-warn';
}

function riskLabel(risk: string): string {
  return risk.charAt(0).toUpperCase() + risk.slice(1);
}

function agentStatus(agent: TrustAgent): TrustAgent['status'] {
  if (frozenAgentIds.has(agent.id)) return 'frozen';
  return agent.status === 'frozen' ? 'active' : agent.status;
}

function Layout({ children }: { children: unknown }) {
  return html`
    <div class="trust-page">
      <div class="trust-page-header">
        <div>
          <div class="trust-kicker">PayBotFin Trust Console</div>
          <h1>${pageTitles[currentPage]}</h1>
          <p>Govern AI agents before they act. Record every allow, deny, and review decision in a tamper-evident audit chain.</p>
        </div>
        <div class="trust-boundary">
          <strong>Governance-only</strong>
          <span>No custody. No human payment execution. Customer keeps AML/KYC/rails.</span>
        </div>
      </div>
      ${children}
    </div>
  `;
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return html`
    <div class="trust-stat">
      <div class=${'trust-stat-value ' + (tone || '')}>${value}</div>
      <div class="trust-stat-label">${label}</div>
    </div>
  `;
}

function StatusPill({ value }: { value: string }) {
  return html`<span class=${'trust-status ' + statusClass(value)}>${value}</span>`;
}

function Overview() {
  const total = decisions.length;
  const allowed = decisions.filter(decision => decision.status === 'allow').length;
  const denied = decisions.filter(decision => decision.status === 'deny').length;
  const highRisk = decisions.filter(decision => decision.risk === 'high').length;
  const chainStatus = getChainStatus(auditTampered);
  return html`
    <${Layout}>
      <div class="trust-grid trust-grid-5">
        <${Stat} label="Decisions today" value=${String(total)} />
        <${Stat} label="Allow rate" value=${Math.round((allowed / total) * 100) + '%'} tone="trust-text-ok" />
        <${Stat} label="Denied" value=${String(denied)} tone="trust-text-bad" />
        <${Stat} label="Active agents" value=${String(agents.length - frozenAgentIds.size)} />
        <${Stat} label="High-risk actions" value=${String(highRisk)} tone="trust-text-warn" />
      </div>
      <div class="trust-two-col">
        <section class="trust-panel">
          <div class="trust-panel-head">
            <h2>Audit Chain</h2>
            <${StatusPill} value=${chainStatus} />
          </div>
          <p class="trust-muted">The console treats the audit chain as evidence, not a payment receipt. Tamper detection belongs to the proof story for banks, fintechs, gaming, and e-commerce.</p>
          <div class="trust-chain-strip">
            ${auditEntries.map((entry, index) => html`
              <div class=${'trust-chain-node ' + (auditTampered && index === 1 ? 'trust-chain-tampered' : '')}>
                <strong>#${entry.seq}</strong>
                <span>${entry.decision}</span>
              </div>
            `)}
          </div>
        </section>
        <section class="trust-panel">
          <div class="trust-panel-head">
            <h2>Latest Policy Violations</h2>
            <span class="trust-muted">${denied + 1} open items</span>
          </div>
          <div class="trust-list">
            ${decisions.filter(decision => decision.status !== 'allow').map(decision => html`
              <div class="trust-list-row">
                <div>
                  <strong>${decision.action}</strong>
                  <p>${decision.reason}</p>
                </div>
                <${StatusPill} value=${decision.status} />
              </div>
            `)}
          </div>
        </section>
      </div>
      <section class="trust-panel">
        <div class="trust-panel-head">
          <h2>Business Model Readiness</h2>
          <span class="trust-muted">Same console, different deployment posture</span>
        </div>
        <div class="trust-grid trust-grid-4">
          <div class="trust-card"><strong>Banks</strong><p>Reference UI plus licensed/self-hosted trust infrastructure. API-first and SIEM-ready.</p></div>
          <div class="trust-card"><strong>Fintechs</strong><p>Hosted console with API keys, policy editing, and fast operational visibility.</p></div>
          <div class="trust-card"><strong>Partners</strong><p>Embeddable proof widgets and white-label tenant views.</p></div>
          <div class="trust-card"><strong>TaaS</strong><p>Managed monitoring, audit packs, RBAC, tenant controls, and exports.</p></div>
        </div>
      </section>
    </${Layout}>
  `;
}

function AgentsPage() {
  return html`
    <${Layout}>
      <div class="trust-grid trust-grid-2">
        ${agents.map(agent => {
          const status = agentStatus(agent);
          return html`
            <section class="trust-panel">
              <div class="trust-panel-head">
                <div>
                  <h2>${agent.label}</h2>
                  <p class="trust-mono">${agent.id}</p>
                </div>
                <${StatusPill} value=${status} />
              </div>
              <div class="trust-field-grid">
                <div><span>Operator</span><strong>${agent.operatorRef}</strong></div>
                <div><span>Mandate</span><strong>${agent.mandateRef}</strong></div>
                <div><span>Trust level</span><strong>${agent.trustLevel}</strong></div>
                <div><span>Risk</span><strong>${riskLabel(agent.risk)}</strong></div>
                <div><span>Spend/action limit</span><strong>${agent.spendLimit}</strong></div>
                <div><span>Velocity limit</span><strong>${agent.velocityLimit}</strong></div>
              </div>
              <div class="trust-chip-row">
                ${agent.allowedActions.map(action => html`<span class="trust-chip">${action}</span>`)}
              </div>
              <button class=${'trust-button ' + (status === 'frozen' ? 'trust-secondary' : 'trust-danger')} data-toggle-agent=${agent.id}>
                ${status === 'frozen' ? 'Unfreeze agent' : 'Freeze agent'}
              </button>
            </section>
          `;
        })}
      </div>
    </${Layout}>
  `;
}

function DecisionsPage() {
  return html`
    <${Layout}>
      <section class="trust-panel">
        <div class="trust-panel-head">
          <h2>Decision Stream</h2>
          <span class="trust-muted">This is the customer-facing view of POST /authorize outcomes.</span>
        </div>
        <div class="trust-table">
          <div class="trust-table-head">
            <span>Time</span><span>Agent</span><span>Action</span><span>Policy</span><span>Decision</span><span>Audit hash</span>
          </div>
          ${decisions.map(decision => html`
            <div class="trust-table-row">
              <span>${new Date(decision.timestamp).toLocaleTimeString()}</span>
              <span class="trust-mono">${decision.agentId}</span>
              <span><strong>${decision.action}</strong><small>${decision.resource}</small></span>
              <span>${decision.policy}</span>
              <span><${StatusPill} value=${decision.status} /></span>
              <span class="trust-mono">${decision.auditHash.slice(0, 14)}...</span>
            </div>
            <div class="trust-table-detail">${decision.reason}</div>
          `)}
        </div>
      </section>
    </${Layout}>
  `;
}

function PolicyPage() {
  const result = evaluatePolicy(playground.operation, playground.amount, playground.resource);
  return html`
    <${Layout}>
      <div class="trust-two-col">
        <section class="trust-panel">
          <div class="trust-panel-head">
            <h2>Policy Packs</h2>
            <span class="trust-muted">Versioned controls per customer or partner tenant.</span>
          </div>
          <div class="trust-list">
            ${policyPacks.map(policy => html`
              <div class="trust-list-row trust-policy-row">
                <div>
                  <strong>${policy.name}</strong>
                  <p>${policy.description}</p>
                  <div class="trust-chip-row">
                    ${policy.allowedOperations.map(operation => html`<span class="trust-chip">${operation}</span>`)}
                  </div>
                </div>
                <div class="trust-policy-meta">
                  <span>${policy.velocityLimit}</span>
                  <span>${policy.amountLimit}</span>
                </div>
              </div>
            `)}
          </div>
        </section>
        <section class="trust-panel">
          <div class="trust-panel-head">
            <h2>Policy Test Playground</h2>
            <${StatusPill} value=${result.status} />
          </div>
          <label class="trust-input-label">Operation</label>
          <select id="play-operation" class="trust-input">
            ${['reconcile_invoice', 'request_refund', 'grant_currency', 'wire_transfer'].map(operation => html`
              <option value=${operation} selected=${operation === playground.operation}>${operation}</option>
            `)}
          </select>
          <label class="trust-input-label">Amount</label>
          <input id="play-amount" class="trust-input" type="number" value=${String(playground.amount)} />
          <label class="trust-input-label">Resource</label>
          <input id="play-resource" class="trust-input" value=${playground.resource} />
          <div class="trust-result">
            <strong>${result.status.toUpperCase()}</strong>
            <p>${result.reason}</p>
          </div>
        </section>
      </div>
    </${Layout}>
  `;
}

function AuditPage() {
  const chainStatus = getChainStatus(auditTampered);
  return html`
    <${Layout}>
      <section class="trust-panel">
        <div class="trust-panel-head">
          <div>
            <h2>Replay And Verify</h2>
            <p class="trust-muted">The money shot: modify one record and the chain becomes invalid.</p>
          </div>
          <${StatusPill} value=${chainStatus} />
        </div>
        <div class="trust-action-row">
          <button class="trust-button" id="verify-chain">Replay chain</button>
          <button class="trust-button trust-danger" id="tamper-chain">Tamper with entry #1041</button>
          <button class="trust-button trust-secondary" id="reset-chain">Reset evidence</button>
          <button class="trust-button trust-secondary" id="export-evidence">Export evidence package</button>
        </div>
        <div class="trust-table trust-audit-table">
          <div class="trust-table-head">
            <span>Seq</span><span>Event</span><span>Actor</span><span>Decision</span><span>Previous hash</span><span>Event hash</span>
          </div>
          ${auditEntries.map((entry, index) => html`
            <div class=${'trust-table-row ' + (auditTampered && index === 1 ? 'trust-row-tampered' : '')}>
              <span>#${entry.seq}</span>
              <span>${entry.eventType}</span>
              <span class="trust-mono">${entry.actor}</span>
              <span><${StatusPill} value=${entry.decision} /></span>
              <span class="trust-mono">${entry.previousHash.slice(0, 12)}...</span>
              <span class="trust-mono">${auditTampered && index === 1 ? 'tampered-record' : entry.eventHash.slice(0, 14) + '...'}</span>
            </div>
          `)}
        </div>
      </section>
    </${Layout}>
  `;
}

function IntegrationsPage() {
  return html`
    <${Layout}>
      <div class="trust-grid trust-grid-2">
        ${integrations.map(integration => html`
          <section class="trust-panel">
            <div class="trust-panel-head">
              <div>
                <h2>${integration.name}</h2>
                <p class="trust-muted">${integration.type}</p>
              </div>
              <${StatusPill} value=${integration.status} />
            </div>
            <p>${integration.description}</p>
          </section>
        `)}
      </div>
      <section class="trust-panel">
        <div class="trust-panel-head">
          <h2>Deployment Posture</h2>
          <span class="trust-muted">Designed for SaaS, TaaS, self-hosted licensing, and partner embedding.</span>
        </div>
        <div class="trust-grid trust-grid-4">
          <div class="trust-card"><strong>API keys</strong><p>Tenant-scoped keys with permissions for decisions, agents, audit, and exports.</p></div>
          <div class="trust-card"><strong>Webhooks</strong><p>Signed event delivery for allow, deny, review, freeze, and export events.</p></div>
          <div class="trust-card"><strong>SIEM</strong><p>Evidence export target for bank and fintech security teams.</p></div>
          <div class="trust-card"><strong>Adapters</strong><p>Customer-owned AML, KYC, PSD2, and case systems connect around PayBot.</p></div>
        </div>
      </section>
    </${Layout}>
  `;
}

function renderPage(): void {
  const view = {
    overview: Overview,
    agents: AgentsPage,
    decisions: DecisionsPage,
    policy: PolicyPage,
    audit: AuditPage,
    integrations: IntegrationsPage,
  }[currentPage];
  render(html`<${view} />`, content());
  bindPageEvents();
}

function bindPageEvents(): void {
  document.querySelectorAll<HTMLElement>('[data-toggle-agent]').forEach(button => {
    button.addEventListener('click', () => {
      const id = button.dataset.toggleAgent;
      if (!id) return;
      const next = new Set(frozenAgentIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      frozenAgentIds = next;
      renderPage();
    });
  });

  document.getElementById('play-operation')?.addEventListener('change', event => {
    playground = { ...playground, operation: (event.target as HTMLSelectElement).value };
    renderPage();
  });
  document.getElementById('play-amount')?.addEventListener('input', event => {
    playground = { ...playground, amount: Number((event.target as HTMLInputElement).value || 0) };
    renderPage();
  });
  document.getElementById('play-resource')?.addEventListener('input', event => {
    playground = { ...playground, resource: (event.target as HTMLInputElement).value };
    renderPage();
  });

  document.getElementById('tamper-chain')?.addEventListener('click', () => {
    auditTampered = true;
    renderPage();
  });
  document.getElementById('reset-chain')?.addEventListener('click', () => {
    auditTampered = false;
    renderPage();
  });
  document.getElementById('verify-chain')?.addEventListener('click', () => {
    renderPage();
  });
  document.getElementById('export-evidence')?.addEventListener('click', () => {
    const payload = {
      product: 'PayBot Trust Console',
      mode: 'governance-only',
      chainStatus: getChainStatus(auditTampered),
      exportedAt: new Date().toISOString(),
      entries: auditEntries.length,
    };
    void navigator.clipboard?.writeText(JSON.stringify(payload, null, 2));
  });
}

function setActiveNav(page: Page): void {
  document.querySelectorAll<HTMLElement>('[data-page]').forEach(link => {
    link.classList.toggle('active', link.dataset.page === page);
  });
}

document.addEventListener('click', event => {
  const link = (event.target as HTMLElement).closest<HTMLElement>('[data-page]');
  if (!link) return;
  event.preventDefault();
  const page = link.dataset.page as Page;
  if (!pageTitles[page]) return;
  currentPage = page;
  setActiveNav(page);
  renderPage();
});

renderPage();
